import { createRouter, createWebHistory } from "vue-router";
import type { Router, RouterHistory, RouteRecordRaw, RouteMeta } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useNavigationLoaderStore } from "@/stores/navigationLoader";
import { useOrganizationConfig } from "@/stores/organizationConfig";
import { waitForAuthReady } from "@/hooks/useStoreReadiness";

import { rootRoutes } from "./routes/root";
import { mimboxRoutes } from "./routes/mimbox";
import { adminRoutes } from "./routes/admin";
import { qrCodeRoutes } from "./routes/qrCode";
import { fleetManagerRoutes } from "./routes/fleetManager";
import { washingMachineRoutes } from "./routes/washingMachine";
import { userRoutes } from "./routes/user";
import { reportRoutes } from "./routes/reports";
import { miscRoutes } from "./routes/misc";
import { custAndOrgRoutes } from "./routes/custAndOrg";
import { staffRoutes } from "./routes/staff";
import { guidesRoutes } from "./routes/guides";
import { organizationOrCustomerExists } from "./guards";
import { isChunkLoadError, reloadOnce } from "./chunkReload";
import { trackMimboxTableQuery } from "./mimboxTableQuery";

/*
 * We might want to redo the flow structure of guards like this:
 * beforeResolve func will instead of returning a path if blocked throw custom errors
 * then use Router.onError to catch those errors and do something accordingly?
 *  Maybe always redirect to 404 and show why we ended up there in a clearer way?
 *   we could even just block the navigation and show a toast message - NOT ALLOWED or something similar if the block does not need to result in a full navigation
 *
 *
 * Improvments to the logical structure:
 * - Make a registry where the keys are the meta keys and values are a guard function to encapsulate the logic within the function
 *    and make the full path to the guards more searchable
 * */

const routes = [
  ...rootRoutes,
  ...mimboxRoutes,
  ...adminRoutes,
  ...qrCodeRoutes,
  ...fleetManagerRoutes,
  ...washingMachineRoutes,
  ...userRoutes,
  ...reportRoutes,
  ...miscRoutes,
  ...custAndOrgRoutes,
  ...staffRoutes,
  ...guidesRoutes,
] as RouteRecordRaw[];

export const buildRouter = (
  history: RouterHistory = createWebHistory(import.meta.env.BASE_URL),
): Router =>
  createRouter({
    history,
    routes,
  });

const router = buildRouter();

async function featureRequestMet(
  required: Required<RouteMeta>["requiresFeatures"],
): Promise<boolean> {
  const authStore = useAuthStore();

  if (authStore.is.staff()) return true;
  for (const key in required) {
    const typedKey = key as keyof RouteMeta["requiresFeatures"];
    const want = required[typedKey];
    const have = authStore.entity.config.getFeature(typedKey);
    if (want != have) {
      return false;
    }
  }
  return true;
}
// registered first: guards run in order, so the loader is visible while the
// guards below wait (e.g. for auth/config on a slow connection)
let navigating = false;
router.beforeEach((_to, _from) => {
  navigating = true;
  const loadingStore = useNavigationLoaderStore();
  loadingStore.show();
});

const QR_CODE_PATHS = qrCodeRoutes.map((route) => route.path);

router.beforeEach(async (to, from) => {
  if (to.name !== "connection-error" && !(await waitForAuthReady())) {
    return { name: "connection-error", query: { redirect: to.fullPath } };
  }

  const authStore = useAuthStore();

  // The QR code pages load their box's config for viewers outside the tree. Back in the app, a
  // signed-in user gets their own config again.
  const onQrCodePage = to.matched.some((route) => QR_CODE_PATHS.includes(route.path));
  if (useOrganizationConfig().outsideTree && !onQrCodePage) {
    await authStore.reloadEntity();
  }

  if (to.meta.requiresAuth && !authStore.is.loggedIn()) {
    return { path: "/login", query: { redirect: to.fullPath } };
  }

  if (to.meta.requiresFeatures && !(await featureRequestMet(to.meta.requiresFeatures))) {
    return { path: "/mimbox" };
  }

  if (to.meta.requiresStaff && !authStore.is.staff()) {
    return { path: "/mimbox" };
  }

  if (to.meta.requiresNotStaff && !authStore.is.staff()) {
    return { path: "/mimbox" };
  }

  if (to.meta.requiresPasscode && !authStore.is.staff() && !authStore.is.passcode()) {
    const id = to.params.id as string;
    return {
      name: "qr-code-passcode",
      params: { id },
      query: { redirect: to.fullPath },
    };
  }
  if (to.meta.requiresOrgOrCust) {
    return await organizationOrCustomerExists(to, from);
  }
});

router.afterEach((to, from, failure) => {
  navigating = false;
  if (!failure) trackMimboxTableQuery(to, from);
  const loadingStore = useNavigationLoaderStore();
  loadingStore.hide();
});

// afterEach is not called when a guard throws. Registering onError replaces vue-router's
// default error log, so log it here
router.onError((error, to) => {
  navigating = false;
  const loadingStore = useNavigationLoaderStore();
  loadingStore.hide();
  if (isChunkLoadError(error) && reloadOnce(to?.fullPath)) return;
  console.error(error);
});

// Vite fires this when a chunk from an old build fails to load after a deploy. Preventing it
// makes the import resolve to undefined, so during a navigation let it fail instead and
// router.onError reloads to the route being navigated to. Other lazy imports reload in place
window.addEventListener("vite:preloadError", (event) => {
  if (navigating) return;
  if (reloadOnce()) event.preventDefault();
});

export default router;
